import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, MapPin, Package, Wrench, Search, Flame, Share2 } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import UserBadges from '@/components/UserBadges';

const conditionLabels = { nuevo:'Nuevo', como_nuevo:'Como nuevo', bueno:'Buen estado', usado:'Usado' };

const getMediaUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${url}`;
};

export default function PublicProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/users/${userId}/public`).then(r => {
      setProfile(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!profile) return <div className="min-h-screen bg-background flex flex-col items-center justify-center p-8"><p className="text-muted-foreground font-body">Usuario no encontrado</p></div>;

  const { user, items } = profile;
  const availableCount = items.filter(i => i.status === 'available').length;
  const soldCount = items.filter(i => i.status === 'sold').length;

  const handleShareProfile = async () => {
    const url = `${window.location.origin}/user/${userId}`;
    const shareData = { title: `${user.name} en Bisne`, text: `Mira los articulos de ${user.name} en Bisne`, url };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch {}
    } else {
      try { await navigator.clipboard.writeText(url); toast.success('Link del perfil copiado!'); } catch { toast.error('Error al copiar'); }
    }
  };

  return (
    <div data-testid="public-profile-page" className="min-h-screen bg-background page-with-nav">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b-2 border-border px-4 py-3 flex items-center gap-3">
        <button data-testid="public-profile-back" onClick={() => navigate(-1)} className="min-w-[44px] min-h-[44px] flex items-center justify-center">
          <ArrowLeft className="w-6 h-6 text-foreground" />
        </button>
        <span className="font-heading text-lg text-primary uppercase truncate">Perfil</span>
      </div>

      {/* User info */}
      <div className="px-4 py-6 flex flex-col items-center gap-3 border-b-2 border-border">
        <Avatar className="w-20 h-20 border-3 border-primary">
          <AvatarImage src={user.picture?.startsWith('http') ? user.picture : getMediaUrl(user.picture)} />
          <AvatarFallback className="bg-secondary text-foreground text-2xl font-heading">{user.name?.charAt(0)}</AvatarFallback>
        </Avatar>
        <div className="text-center">
          <h1 data-testid="public-profile-name" className="font-heading text-2xl text-foreground uppercase">{user.name}</h1>
          <div className="flex items-center justify-center gap-1.5 mt-1">
            <UserBadges user={user} size="sm" />
            <span className="text-muted-foreground text-xs font-body">Miembro de Bisne</span>
          </div>
        </div>
        <div className="flex gap-4 mt-2">
          <div className="text-center">
            <p className="font-heading text-xl text-primary">{availableCount}</p>
            <p className="text-muted-foreground text-[10px] font-body uppercase tracking-wider">Activos</p>
          </div>
          <div className="w-px bg-border" />
          <div className="text-center">
            <p className="font-heading text-xl text-zinc-400">{soldCount}</p>
            <p className="text-muted-foreground text-[10px] font-body uppercase tracking-wider">Vendidos</p>
          </div>
        </div>
        <Button data-testid="public-profile-share" onClick={handleShareProfile}
          className="mt-3 rounded-sm font-body font-bold uppercase text-sm bg-primary text-primary-foreground hover:bg-primary/90 border-2 border-primary px-6 h-10">
          <Share2 className="w-4 h-4 mr-2" /> Compartir Perfil
        </Button>
      </div>
      <div className="px-4 py-4">
        <h2 className="font-heading text-lg text-foreground uppercase mb-4">Articulos ({items.length})</h2>
        {items.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <p className="text-muted-foreground font-body text-sm">Este usuario no tiene articulos</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {items.map(item => {
              const isSold = item.status === 'sold' && item.item_type !== 'service';
              const isWanted = item.item_type === 'wanted';
              const isService = item.item_type === 'service';
              const img = item.images?.[0] || item.image_url;
              return (
                <div key={item.item_id} data-testid={`public-item-${item.item_id}`}
                  onClick={() => navigate(`/item/${item.item_id}`)}
                  className={`cursor-pointer overflow-hidden border-2 transition-all hover:-translate-y-0.5 ${
                    isWanted ? 'bg-[#FFD700] border-black' : isSold ? 'border-zinc-700 opacity-60' : 'border-zinc-700 hover:border-primary/50'
                  }`}>
                  {/* Image */}
                  <div className={`aspect-square relative ${isWanted ? 'bg-black/10' : 'bg-zinc-900'}`}>
                    {img ? (
                      <img src={getMediaUrl(img)} className={`object-cover w-full h-full ${isSold ? 'grayscale' : ''}`} alt={item.title} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {isWanted ? <Search className="w-8 h-8 text-black/30" /> : <Package className="w-8 h-8 text-zinc-700" />}
                      </div>
                    )}
                    {/* Sold stamp */}
                    {isSold && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-red-600 px-3 py-1 border-2 border-red-800" style={{ transform: 'rotate(-15deg)' }}>
                          <span className="font-heading text-xs text-white uppercase tracking-wider">YA SE ARMO!</span>
                        </div>
                      </div>
                    )}
                    {/* Type badge */}
                    <div className="absolute top-1.5 left-1.5">
                      {isWanted ? (
                        <Badge className="rounded-none bg-black text-[#FFD700] border-0 font-body text-[8px] px-1.5 py-0.5"><Search className="w-2.5 h-2.5 mr-0.5" />Busca</Badge>
                      ) : isService ? (
                        <Badge className="rounded-none bg-orange-500 text-white border-0 font-body text-[8px] px-1.5 py-0.5"><Wrench className="w-2.5 h-2.5 mr-0.5" />Servicio</Badge>
                      ) : null}
                    </div>
                  </div>
                  {/* Info */}
                  <div className={`p-2.5 ${isWanted ? 'text-black' : ''}`}>
                    <h3 className={`font-heading text-sm uppercase leading-tight truncate ${isWanted ? 'text-black' : 'text-foreground'}`}>{item.title}</h3>
                    <p className={`font-heading text-base mt-0.5 ${isWanted ? 'text-black' : 'text-primary'}`}>
                      {isWanted ? 'Hasta ' : ''}${item.price?.toLocaleString()} MXN
                    </p>
                    <div className="flex items-center gap-1.5 mt-1">
                      {item.location && (
                        <span className={`text-[9px] font-body flex items-center gap-0.5 ${isWanted ? 'text-black/50' : 'text-muted-foreground'}`}>
                          <MapPin className="w-2.5 h-2.5" />{item.location}
                        </span>
                      )}
                      {item.offer_count > 0 && (
                        <span className="text-[9px] font-body flex items-center gap-0.5 text-red-400">
                          <Flame className="w-2.5 h-2.5" />{item.offer_count}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
